import { useEffect, useMemo, useState } from "react";
import type { Task } from "@shared/domain";
import { Button } from "@/components/ui/Button";
import { InputField } from "@/components/ui/InputField";
import { Sheet } from "@/components/ui/Sheet";
import { useGenerateSubtasksMutation, useRemoteAgentApprovalMutation, useUpdateTaskMutation } from "@/hooks/useBackendData";
import { useAppStore } from "@/state/appStore";
import { parseTaskDetails } from "@/utils/tasks";

interface Props {
  open: boolean;
  onClose: () => void;
  taskId?: string;
}

const assigneeToSelection = (assignee: Task["assignee"]): string => {
  if (assignee.type === "agent") return `agent:${assignee.agentId}`;
  if (assignee.type === "member") return `member:${assignee.userId}`;
  return "auto";
};

const parseAssigneeSelection = (value: string): Task["assignee"] => {
  if (value === "auto") return { type: "auto", strategy: "skill_match" };
  const [kind, id] = value.split(":", 2);
  if (kind === "agent" && id) return { type: "agent", agentId: id };
  if (kind === "member" && id) return { type: "member", userId: id, role: "editor" };
  return { type: "auto", strategy: "skill_match" };
};

export const EditTaskSheet = ({ open, onClose, taskId }: Props): JSX.Element | null => {
  const task = useAppStore((state) => state.tasks.find((item) => item.id === taskId));
  const projects = useAppStore((state) => state.projects);
  const agents = useAppStore((state) => state.agents);
  const members = useAppStore((state) => state.members);
  const pendingAgentApproval = useAppStore((state) => (taskId ? state.pendingAgentApprovals[taskId] : undefined));
  const updateTaskMutation = useUpdateTaskMutation();
  const regenerateMutation = useGenerateSubtasksMutation();
  const remoteAgentApprovalMutation = useRemoteAgentApprovalMutation(taskId);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [definitionOfDone, setDefinitionOfDone] = useState("");
  const [dueAt, setDueAt] = useState("");
  const [attachmentNames, setAttachmentNames] = useState<string[]>([]);
  const [projectId, setProjectId] = useState("");
  const [assigneeSelection, setAssigneeSelection] = useState("auto");
  const [regeneratePlan, setRegeneratePlan] = useState(false);
  const [autoApproveRemoteAgentActions, setAutoApproveRemoteAgentActions] = useState(false);

  useEffect(() => {
    if (!open || !task) return;
    const details = parseTaskDetails(task.description);
    setTitle(task.title);
    setDescription(details.summary ?? "");
    setDefinitionOfDone(details.definitionOfDone ?? "");
    setDueAt(task.dueAt ?? "");
    setAttachmentNames(task.attachments ?? []);
    setProjectId(task.projectId);
    setAssigneeSelection(assigneeToSelection(task.assignee));
    setAutoApproveRemoteAgentActions(task.autoApproveRemoteAgentActions);
    setRegeneratePlan(false);
  }, [open, task]);

  const canSubmit = title.trim().length >= 2 && Boolean(task);

  const planningCriticalChanged = useMemo(() => {
    if (!task) return false;
    const details = parseTaskDetails(task.description);
    return (
      title.trim() !== task.title.trim() ||
      description.trim() !== (details.summary ?? "").trim() ||
      definitionOfDone.trim() !== (details.definitionOfDone ?? "").trim()
    );
  }, [definitionOfDone, description, task, title]);

  if (!open || !task) return null;

  return (
    <Sheet open={open} title="Edit Task" onClose={onClose} height="80">
      <div className="space-y-3">
        <InputField label="Title *" inputProps={{ value: title, onChange: (event) => setTitle(event.target.value) }} />
        <InputField
          kind="textarea"
          label="Description"
          textareaProps={{ value: description, onChange: (event) => setDescription(event.target.value), placeholder: "Task context" }}
        />
        <InputField
          kind="textarea"
          label="Definition of done"
          textareaProps={{ value: definitionOfDone, onChange: (event) => setDefinitionOfDone(event.target.value), placeholder: "Completion criteria" }}
        />
        <InputField label="Due date" inputProps={{ type: "datetime-local", value: dueAt, onChange: (event) => setDueAt(event.target.value) }} />
        <InputField
          label="Attachments"
          inputProps={{
            type: "file",
            multiple: true,
            onChange: (event) => {
              const files = Array.from(event.target.files ?? []);
              setAttachmentNames(files.map((file) => file.name));
            }
          }}
        />
        {attachmentNames.length > 0 ? (
          <div className="flex items-center justify-between gap-2">
            <p className="min-w-0 text-xs text-[var(--text-secondary)]">Selected: {attachmentNames.join(", ")}</p>
            <button
              type="button"
              className="text-xs font-medium text-[var(--accent-primary)]"
              onClick={() => setAttachmentNames([])}
            >
              Clear
            </button>
          </div>
        ) : (
          <p className="text-xs text-[var(--text-tertiary)]">Optional. File upload is queued for processing after task save.</p>
        )}

        <label className="block text-sm text-[var(--text-secondary)]">
          Project
          <select className="input mt-1" value={projectId} onChange={(event) => setProjectId(event.target.value)}>
            {projects.map((project) => (
              <option key={project.id} value={project.id}>
                {project.title}
              </option>
            ))}
          </select>
        </label>

        <label className="flex items-start gap-3 rounded-lg border border-[var(--border-default)] bg-[var(--bg-secondary)] p-3">
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 shrink-0 accent-[var(--accent-primary)]"
            checked={autoApproveRemoteAgentActions}
            onChange={(event) => setAutoApproveRemoteAgentActions(event.target.checked)}
          />
          <span className="min-w-0">
            <span className="block text-sm font-medium text-[var(--text-primary)]">Auto-approve remote agent requests</span>
            <span className="mt-1 block text-xs leading-5 text-[var(--text-secondary)]">
              Approves every Hermes request for this task, including side-effectful actions. Human publish, send, and payment gates still require review.
            </span>
          </span>
        </label>

        <label className="block text-sm text-[var(--text-secondary)]">
          Assign to
          <select className="input mt-1" value={assigneeSelection} onChange={(event) => setAssigneeSelection(event.target.value)}>
            <option value="auto">Auto-assign (AI)</option>
            {agents.length > 0 ? (
              <optgroup label="AI Agents">
                {agents.map((agent) => (
                  <option key={agent.id} value={`agent:${agent.id}`}>
                    {agent.name}
                  </option>
                ))}
              </optgroup>
            ) : null}
            {members.length > 0 ? (
              <optgroup label="Members">
                {members.map((member) => (
                  <option key={member.userId} value={`member:${member.userId}`}>
                    {member.name} ({member.role})
                  </option>
                ))}
              </optgroup>
            ) : null}
          </select>
        </label>

        {planningCriticalChanged ? (
          <label className="flex items-center gap-2 rounded-lg bg-[var(--bg-tertiary)] p-2 text-sm text-[var(--text-secondary)]">
            <input type="checkbox" checked={regeneratePlan} onChange={(event) => setRegeneratePlan(event.target.checked)} />
            Re-generate subtasks after save
          </label>
        ) : null}

        <Button
          className="w-full"
          disabled={!canSubmit || updateTaskMutation.isPending || remoteAgentApprovalMutation.isPending}
          onClick={async () => {
            const details = parseTaskDetails(task.description);
            const nextAssignee = parseAssigneeSelection(assigneeSelection);
            const normalizedAttachments = attachmentNames
              .map((name) => name.trim())
              .filter((name) => name.length > 0)
              .slice(0, 10);
            const currentAttachments = (task.attachments ?? [])
              .map((name) => name.trim())
              .filter((name) => name.length > 0)
              .slice(0, 10);
            const patch: Partial<
              Pick<Task, "title" | "description" | "dueAt" | "assignee" | "projectId" | "attachments" | "autoApproveRemoteAgentActions"> & {
                definitionOfDone: string;
              }
            > = {};

            if (title.trim() !== task.title.trim()) patch.title = title.trim();
            if (description.trim() !== (details.summary ?? "").trim()) patch.description = description.trim();
            if (definitionOfDone.trim() !== (details.definitionOfDone ?? "").trim()) patch.definitionOfDone = definitionOfDone.trim();
            if ((dueAt || "") !== (task.dueAt ?? "")) patch.dueAt = dueAt || undefined;
            if (JSON.stringify(normalizedAttachments) !== JSON.stringify(currentAttachments)) patch.attachments = normalizedAttachments;
            if (projectId !== task.projectId) patch.projectId = projectId;
            if (JSON.stringify(nextAssignee) !== JSON.stringify(task.assignee)) patch.assignee = nextAssignee;
            if (autoApproveRemoteAgentActions !== task.autoApproveRemoteAgentActions) {
              patch.autoApproveRemoteAgentActions = autoApproveRemoteAgentActions;
            }

            if (Object.keys(patch).length === 0) {
              onClose();
              return;
            }

            try {
              await updateTaskMutation.mutateAsync({ id: task.id, patch });
              if (autoApproveRemoteAgentActions && !task.autoApproveRemoteAgentActions && pendingAgentApproval) {
                await remoteAgentApprovalMutation.mutateAsync({ runId: pendingAgentApproval.runId, choice: "once" });
              }
              if (regeneratePlan && planningCriticalChanged) {
                await regenerateMutation.mutateAsync(task.id);
              }
              onClose();
            } catch {
              // toast emitted by mutation hooks
            }
          }}
        >
          {updateTaskMutation.isPending || remoteAgentApprovalMutation.isPending ? "Saving..." : "Save Changes"}
        </Button>
      </div>
    </Sheet>
  );
};
